import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  IconButton,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  Button,
} from '@mui/material';
import { ArrowBack, Edit } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { contentService } from './content.service';
import { formatDate, formatDateTime } from '@/helpers';
import { Content } from './content.dto';

const ViewContent = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token, logout } = useAuth();
  const [content, setContent] = useState<Content | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    contentService.setLogoutCallback(logout);

    const fetchData = async () => {
      if (!token || !id) return;

      try {
        setLoading(true);
        const contentData = await contentService.getContent(
          token,
          parseInt(id)
        );
        console.log('Fetched content data:', contentData);
        setContent(contentData);
      } catch (err) {
        console.error('Error fetching content:', err);
        navigate('/content');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, id, navigate, logout]);

  const getStatusColor = (status: boolean) => {
    return status ? 'success' : 'warning';
  };

  const getStatusText = (status: boolean) => {
    return status ? 'Active' : 'Inactive';
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading content...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!content) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h6">Content not found</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <IconButton onClick={() => navigate('/content')} sx={{ mr: 2 }}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h4" component="h1">
                View Content
              </Typography>
            </Box>
            <Box display="flex" gap={2} alignItems="center">
              <Chip
                label={getStatusText(content.status)}
                color={getStatusColor(content.status)}
                variant="outlined"
              />
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => navigate(`/content/edit/${content.id}`)}
              >
                Edit
              </Button>
            </Box>
          </Box>
        </Paper>

        <Card>
          <CardContent>
            {/* Title */}
            <Box mb={3}>
              <Typography variant="overline" color="text.secondary">
                Title
              </Typography>
              <Typography variant="h4" gutterBottom>
                {content.title}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Slug */}
            <Box mb={3}>
              <Typography variant="overline" color="text.secondary">
                Slug
              </Typography>
              <Typography variant="h6">{content.slug}</Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Content */}
            <Box mb={3}>
              <Typography variant="overline" color="text.secondary">
                Content
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  lineHeight: 1.8,
                  '& p': { margin: '8px 0' },
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    margin: '16px 0 8px 0',
                  },
                  '& ul, & ol': { paddingLeft: '24px', margin: '8px 0' },
                  '& blockquote': {
                    borderLeft: '4px solid #ccc',
                    paddingLeft: '16px',
                    margin: '16px 0',
                    color: 'text.secondary',
                  },
                }}
                dangerouslySetInnerHTML={{ __html: content.content }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* SEO Information */}
            {/* <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              SEO Information
            </Typography>

            <Box mb={3}>
              <Typography variant="overline" color="text.secondary">
                Meta Description
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {content.metaDescription || 'N/A'}
              </Typography>
            </Box>

            <Box mb={3}>
              <Typography variant="overline" color="text.secondary">
                Meta Keywords
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {content.metaKeywords || 'N/A'}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} /> */}

            {/* Metadata */}
            <Box display="flex" gap={4} flexWrap="wrap">
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(content.createdAt)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Updated At
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(content.updatedAt)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ViewContent;
